import { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Spin, Select, Row, Col, Empty } from "antd";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";
import {
    DollarOutlined, MinusCircleOutlined, RiseOutlined, TeamOutlined,
} from "@ant-design/icons";

const { Option } = Select;

const MONTHS = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December",
];
const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const fmtCurrency = (v) =>
    `INR ${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

export default function PayrollSummary() {
    const { authData, backUrl } = useContext(AuthContext) || { backUrl: "" };
    const currentMonth = now.getMonth() + 1;

    const [organisations, setOrganisations] = useState([]);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [organisationId, setOrganisationId] = useState(null);
    const [month, setMonth] = useState(currentMonth);
    const [year, setYear] = useState(CURRENT_YEAR);

    useEffect(() => {
        axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: { Authorization: `Bearer ${authData?.access}` },
        }).then(res => {
            const orgs = res.data.results || res.data || [];
            setOrganisations(orgs);
            if (orgs.length > 0) setOrganisationId(orgs[0].id);
        }).catch(() => {});
    }, [backUrl, authData?.access]);

    useEffect(() => {
        if (!organisationId) return;
        setLoading(true);
        axios.get(
            `${backUrl}/api/reports/payroll-summary/?organisation_id=${organisationId}&month=${month}&year=${year}`,
            { headers: { Authorization: `Bearer ${authData?.access}` } }
        ).then(res => setData(res.data))
         .catch(() => setData(null))
         .finally(() => setLoading(false));
    }, [organisationId, month, year, backUrl, authData?.access]);

    const gross = parseFloat(data?.gross_salary || 0);
    const deductions = parseFloat(data?.total_deductions || 0);
    const net = parseFloat(data?.net_salary || 0);
    const empCount = data?.employee_count ?? 0;

    const chartData = data ? [{
        name: `${MONTHS[month - 1].substring(0, 3)} ${year}`,
        "Gross Salary": gross,
        "Deductions": deductions,
        "Net Salary": net,
    }] : [];

    const kpis = [
        { label: "Gross Salary",     val: fmtCurrency(gross),      color: "#0e497b", bg: "#eff6ff", border: "#bfdbfe" },
        { label: "Deductions",       val: fmtCurrency(deductions),  color: "#ef4444", bg: "#fff1f2", border: "#fecaca" },
        { label: "Net Salary",       val: fmtCurrency(net),         color: "#10b981", bg: "#f0fdf4", border: "#bbf7d0" },
        { label: "Employees",        val: empCount || "-",          color: "#7c3aed", bg: "#faf5ff", border: "#ddd6fe" },
    ];

    const fmtYAxis = (v) => {
        if (v >= 10000000) return `${(v / 10000000).toFixed(1)}Cr`;
        if (v >= 100000) return `${(v / 100000).toFixed(1)}L`;
        if (v >= 1000) return `${(v / 1000).toFixed(0)}K`;
        return `${v}`;
    };

    return (
        <div style={{
            background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: 16,
            border: "1px solid #e2e8f0",
            boxShadow: "0 4px 16px rgba(15,23,42,0.04)",
            overflow: "hidden",
            height: "100%",
        }}>
            <div style={{ height: 3.5, background: "linear-gradient(90deg, #059669, #10b981, #34d399)" }} />
            <div style={{ padding: "16px 20px" }}>
                <Row justify="space-between" align="middle" gutter={[10, 10]} style={{ marginBottom: 16 }}>
                    <Col>
                        <div style={{ fontWeight: 800, fontSize: 15, color: "#0f172a" }}>Payroll Summary</div>
                        <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                            {MONTHS[month - 1]} {year} � Monthly salary cost breakdown
                        </div>
                    </Col>
                    <Col>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <Select value={organisationId} onChange={setOrganisationId} style={{ width: 155 }} size="small" placeholder="Organisation">
                                {organisations.map(o => <Option key={o.id} value={o.id}>{o.name}</Option>)}
                            </Select>
                            <Select value={month} onChange={setMonth} style={{ width: 108 }} size="small">
                                {MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
                            </Select>
                            <Select value={year} onChange={setYear} style={{ width: 80 }} size="small">
                                {YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
                            </Select>
                        </div>
                    </Col>
                </Row>

                {loading ? (
                    <div style={{ textAlign: "center", padding: "60px 0" }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 12, color: "#64748b", fontWeight: 600 }}>Loading Payroll Summary...</div>
                    </div>
                ) : !data ? (
                    <Empty description="No payroll data found for the selected period" style={{ padding: "40px 0" }} />
                ) : (
                    <>
                        <Row gutter={[10, 10]} style={{ marginBottom: 18 }}>
                            {kpis.map(k => (
                                <Col xs={12} sm={6} key={k.label}>
                                    <div style={{
                                        background: k.bg, border: `1px solid ${k.border}`,
                                        borderRadius: 12, padding: "10px 12px", textAlign: "center",
                                    }}>
                                        <div style={{ fontSize: 10, fontWeight: 700, color: k.color, textTransform: "uppercase", marginBottom: 4 }}>
                                            {k.label}
                                        </div>
                                        <div style={{ fontSize: 13, fontWeight: 900, color: k.color }}>
                                            {k.val}
                                        </div>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                        <ResponsiveContainer width="100%" height={260}>
                            <BarChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }} barGap={8}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                                <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12, fontWeight: 600 }} />
                                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={fmtYAxis} />
                                <Tooltip
                                    formatter={v => [fmtCurrency(v)]}
                                    contentStyle={{ borderRadius: 8, border: "1px solid #e2e8f0", boxShadow: "0 4px 12px rgba(15,23,42,0.08)" }}
                                />
                                <Legend wrapperStyle={{ fontSize: 12, paddingTop: 6 }} />
                                <Bar dataKey="Gross Salary" fill="#0e497b" radius={[6, 6, 0, 0]} barSize={52} />
                                <Bar dataKey="Deductions" fill="#ef4444" radius={[6, 6, 0, 0]} barSize={52} />
                                <Bar dataKey="Net Salary" fill="#10b981" radius={[6, 6, 0, 0]} barSize={52} />
                            </BarChart>
                        </ResponsiveContainer>
                    </>
                )}
            </div>
        </div>
    );
}
